
import { supabase } from "@/integrations/supabase/client";
import { Reagent } from "@/types/reagent.types";
import { ensureValidSession, resetSessionState } from "../auth/sessionUtils";
import { handleDatabaseError } from "../utils/errorUtils";
import { createLogger } from "../utils/loggingUtils";
import { toast } from "sonner";
import { mapReagentArray } from "../utils/reagentMapper";

const logger = createLogger('fetchReagents');

// Cache the last successful response to improve user experience
let cachedReagents: Reagent[] | null = null;
let lastFetchTime = 0;
const CACHE_TTL = 60 * 1000; // 1 minute cache TTL

export const fetchReagents = async (): Promise<Reagent[]> => {
  const timeLabel = `fetchReagents-${Date.now()}`;
  logger.info("Starting database query", timeLabel);
  console.time(timeLabel);
  
  try {
    // Check if we have a recent cache that's still valid
    const now = Date.now();
    if (cachedReagents && (now - lastFetchTime < CACHE_TTL)) {
      logger.info(`Using cached reagents (${cachedReagents.length} items, cache age: ${Math.floor((now - lastFetchTime)/1000)}s)`);
      console.timeEnd(timeLabel);
      return cachedReagents;
    }
    
    // First make sure we have a valid session
    const session = await ensureValidSession();
    if (!session) {
      logger.error("No valid session available");
      toast.error("Authentication required. Please log in.");
      return [];
    }
    
    logger.info("Session validated, fetching reagents...");
    
    // Set a timeout to abort the request if it takes too long
    const timeout = 12000; // 12 seconds
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      logger.warn("Request timeout reached, aborting");
      controller.abort();
    }, timeout);
    
    try {
      const { data, error } = await supabase
        .from("reagents")
        .select(`
          *,
          profiles:user_id!inner(*),
          categories:category_id(*)
        `)
        .eq("available", true) // Only return available reagents
        .eq("profiles.approval_status", "approved")
        .neq("profiles.is_deleted", true)
        .order("created_at", { ascending: false })
        .abortSignal(controller.signal);
        
      // Clear the timeout since the request completed
      clearTimeout(timeoutId);
        
      if (error) {
        if (error.code === 'PGRST301' || error.message.includes('JWT')) {
          logger.error("Session expired during fetch:", error);
          // Reset session state to force a complete refresh
          resetSessionState();
          
          // Try to refresh session and retry
          logger.info("Refreshing session and retrying fetch");
          await ensureValidSession();
          
          const { data: retryData, error: retryError } = await supabase
            .from("reagents")
            .select(`
              *,
              profiles:user_id!inner(*),
              categories:category_id(*)
            `)
            .eq("available", true) // Only return available reagents
            .eq("profiles.approval_status", "approved")
            .neq("profiles.is_deleted", true)
            .order("created_at", { ascending: false });
            
          if (retryError) {
            handleDatabaseError(retryError, "Failed to load reagents after retry");
            return cachedReagents || [];
          }
          
          // Map the data using the shared reagent mapper
          const reagentsArray = mapReagentArray(retryData || []);
          
          // Update cache and return data
          cachedReagents = reagentsArray;
          lastFetchTime = Date.now();
          logger.info(`Retry successful, fetched ${cachedReagents.length} reagents`);
          console.timeEnd(timeLabel);
          return cachedReagents;
        }
        
        handleDatabaseError(error, "Failed to fetch reagents");
        return cachedReagents || [];
      }
      
      // Map the data using the shared reagent mapper
      const reagentsArray = mapReagentArray(data || []);
      
      // Update cache with fresh data
      cachedReagents = reagentsArray;
      lastFetchTime = Date.now();
      
      logger.info(`Successfully fetched ${data?.length || 0} reagents`);
      console.timeEnd(timeLabel);
      return reagentsArray;
    } catch (abortError: any) {
      clearTimeout(timeoutId);
      
      if (abortError.name === 'AbortError') {
        logger.error("Request aborted due to timeout");
        
        // Force refresh the session as a last resort
        try {
          logger.info("Request timed out, forcing session refresh");
          resetSessionState();
          const { data: refreshData } = await supabase.auth.refreshSession();
          
          if (refreshData.session) {
            logger.info("Session forcefully refreshed");
            
            // Make one more attempt after forced refresh
            const { data: recoveryData } = await supabase
              .from("reagents")
              .select(`
                *,
                profiles:user_id!inner(*),
                categories:category_id(*)
              `)
              .eq("available", true) // Only return available reagents
              .eq("profiles.approval_status", "approved")
              .neq("profiles.is_deleted", true)
              .order("created_at", { ascending: false });
              
            if (recoveryData) {
              // Map the data using the shared reagent mapper
              const recoveryReagents = mapReagentArray(recoveryData);
              
              // Update cache with recovery data
              cachedReagents = recoveryReagents;
              lastFetchTime = Date.now();
              logger.info(`Recovery succeeded, fetched ${recoveryData.length} reagents`);
              console.timeEnd(timeLabel);
              return recoveryReagents;
            }
          } else {
            logger.warn("Session refresh failed after timeout");
            toast.error("Your session may have expired. Please try logging out and back in.");
          }
        } catch (refreshError) {
          logger.error("Failed to recover after timeout:", refreshError);
        }
        
        // Return cached data if available, or empty array as last resort
        logger.info(`Returning ${cachedReagents?.length || 0} cached reagents after timeout`);
        return cachedReagents || [];
      }
      
      // Re-throw other errors to be caught by outer try-catch
      throw abortError;
    }
  } catch (error) {
    logger.error("Exception in fetchReagents:", error);
    console.timeEnd(timeLabel);
    
    // Return cached data as fallback in case of error
    if (cachedReagents) {
      logger.info(`Returning ${cachedReagents.length} cached reagents after error`);
      return cachedReagents;
    }
    
    throw error;
  }
};

// Add a function to explicitly clear the cache
export const clearReagentsCache = () => {
  cachedReagents = null;
  lastFetchTime = 0;
  logger.info("Reagents cache cleared");
};
